import {
  PreferenceCenter,
  Scope,
} from "@ua/web-sdk/lib/remote-data/preference-center"
import axios from "axios"
import { SubscriptionList } from "../SubscriptionForm/client"
import { ContactListSubscription } from "./clients/ContactClient"

export function createChannelSubscriptions(
  preferenceCenter: PreferenceCenter,
  subscriptions: SubscriptionList[]
): Partial<Record<Scope, Record<string, boolean>>> {
  const output = { email: {} }
  for (const section of preferenceCenter.sections) {
    for (const item of section.items ?? []) {
      if (item.type !== "channel_subscription") continue
      const id = item.subscription_id
      const subscribed = Boolean(
        subscriptions.find((sub) => sub.id === id)?.subscribed
      )
      output.email = { ...output.email, [id]: subscribed }
    }
  }
  return output
}

export function isContactPreferenceCenter(
  preferenceCenter: PreferenceCenter
): boolean {
  return preferenceCenter.sections.some((section) =>
    section.items?.some(
      (item) =>
        item.type === "contact_subscription" ||
        item.type === "contact_subscription_group"
    )
  )
}

export type DeviceType = "email" | "sms" | "ios" | "android" | "amazon" | "open"
const DeviceTypeToScope: { [K in DeviceType]: Scope | null } = {
  email: "email",
  sms: "sms",
  ios: "app",
  android: "app",
  amazon: "app",
  open: null,
}

export function deviceTypeToScope(deviceType: DeviceType): Scope {
  const maybeScope = DeviceTypeToScope[deviceType]
  if (!maybeScope) throw new Error(`unsupported device type ${deviceType}`)

  return maybeScope
}

// this is a minimal channel response; the API returns far more, but this is all
// we need for now
export interface ChannelResponse {
  channel_id: string
  device_type: DeviceType
  installed: boolean
  opt_in: boolean
}

export async function getChannelDetails(
  channelId: string,
  token: string
): Promise<ChannelResponse> {
  const result = await axios.get<{ ok: boolean; channel: ChannelResponse }>(
    `/api/channels/${channelId}`,
    {
      headers: {
        Accept: "application/vnd.urbanairship+json; version=3;",
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (result.status !== 200 || !result.data.ok)
    throw new Error("Unable to retrieve channel")

  return result.data.channel
}

export async function resolveContactId(
  channelId: string,
  deviceType: DeviceType,
  token: string
): Promise<string> {
  const result = await axios.post(
    "/api/contacts/resolve",
    { channel_id: channelId, device_type: deviceType },
    {
      headers: {
        Accept: "application/vnd.urbanairship+json; version=3;",
        Authorization: `Bearer ${token}`,
      },
    }
  )
  if (result.status !== 200 || !result.data.ok)
    throw new Error("Unable to resolve contact from channel ID")

  return result.data.contact_id
}

export function getTimestamp(now: number): string {
  const date = new Date(now)
  return `${date.getUTCFullYear()}-${pad0(date.getUTCMonth() + 1)}-${pad0(
    date.getUTCDate()
  )}T${pad0(date.getUTCHours())}:${pad0(date.getUTCMinutes())}:${pad0(
    date.getUTCSeconds()
  )}`
}

export function pad0(input: string | number): string {
  return input.toString().padStart(2, "0")
}

export function createContactSubscriptions(
  preferenceCenter: PreferenceCenter,
  subscriptions: Readonly<ContactListSubscription[]>
): Partial<Record<Scope, Record<string, boolean>>> {
  const output: Partial<Record<Scope, Record<string, boolean>>> = {}
  preferenceCenter.sections.forEach((section) => {
    if (section.type !== "section") return

    section.items.forEach((item) => {
      if (
        item.type !== "contact_subscription" &&
        item.type !== "contact_subscription_group"
      )
        return

      const scopes =
        item.type === "contact_subscription"
          ? item.scopes
          : item.components.reduce(
              (scopes, component) => scopes.concat(component.scopes),
              [] as Scope[]
            )
      scopes.forEach((scope) => {
        output[scope] = {
          ...output[scope],
          [item.subscription_id]: subscriptions.some(
            (sub) =>
              sub.scope === scope && sub.list_ids.includes(item.subscription_id)
          ),
        }
      })
    })
  })
  return output
}

export function mergeChannelListsWithContactLists(scope: Scope, contactLists: ContactListSubscription[], channelLists: SubscriptionList[]): ContactListSubscription[] {
  const relevantContactSubscriptions = contactLists.filter(list => list.scope == scope)
  const irrelevantContactSubscriptions = contactLists.filter(list => list.scope != scope)
  const channelListIds = channelLists.filter(list => list.subscribed).map(list => list.id)

  if (!relevantContactSubscriptions.length && channelListIds.length) {
    relevantContactSubscriptions.push({
      scope: scope,
      list_ids: channelListIds
    })
  } else {
    relevantContactSubscriptions.forEach(contactList => {
      channelListIds.forEach(listId => {
        if (!contactList.list_ids.includes(listId)) {
          contactList.list_ids.push(listId)
        }
      })
    })
  }

  return [...relevantContactSubscriptions, ...irrelevantContactSubscriptions]
}